import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { useAnimation } from '../../../hooks/useAnimation';

export default function ComplexBuilding() {
  const [showModal, setShowModal] = useState(false);

  const introRef = useAnimation('fade-in-up');
  const mapRef = useAnimation('scale-in');
  const ctaRef = useAnimation('fade-in-up');

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative h-[180px] md:h-[400px] flex items-center justify-center overflow-hidden mt-16 md:mt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e99702599cba4efa55d17449d9970d39.jpg"
            alt="동호수배치도"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2b5c]/70 via-[#1a2b5c]/50 to-[#1a2b5c]/70"></div>
        </div>
        
        <div className="relative z-10 text-center px-4">
          <h1 className="text-[20px] md:text-[48px] font-bold text-white mb-2 md:mb-4" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            동호수배치도
          </h1>
          <p className="text-[11px] md:text-[18px] text-white/90">
            각 동의 세대 배치를 확인하세요
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="bg-gray-50 py-3 md:py-4">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 text-[11px] md:text-[14px]">
            <Link to="/" className="text-gray-500 hover:text-[#1a2b5c] transition-colors whitespace-nowrap">홈</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/complex" className="text-gray-500 hover:text-[#1a2b5c] transition-colors whitespace-nowrap">단지안내</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-[#1a2b5c] font-medium whitespace-nowrap">동호수배치도</span>
          </div>
        </div>
      </section>

      {/* Building Layout Image */}
      <section className="py-8 md:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="text-center mb-6 md:mb-12" ref={introRef}>
            <h2 className="text-[16px] md:text-[38px] font-bold text-[#1a2b5c] mb-2 md:mb-4" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              동호수배치도
            </h2>
            <p className="text-[11px] md:text-[16px] text-gray-600">
              각 동의 세대 배치 및 호수를 확인하실 수 있습니다
            </p>
          </div>

          <div className="relative bg-gray-50 rounded-xl md:rounded-2xl overflow-hidden shadow-lg" ref={mapRef}>
            <img 
              src="https://static.readdy.ai/image/3b7ebc9e513f68fc027b047fbdd0fbe7/566f0a1d64a13a9f2cb81c67695549a9.png"
              alt="동호수배치도"
              className="w-full h-auto"
            />
            <button
              onClick={() => setShowModal(true)}
              className="absolute bottom-3 md:bottom-6 right-3 md:right-6 px-3 md:px-6 py-2 md:py-3 bg-white text-[#1a2b5c] rounded-full text-[11px] md:text-[15px] font-semibold hover:bg-gray-100 transition-all shadow-lg flex items-center gap-1.5 md:gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-zoom-in-line text-sm md:text-lg"></i>
              크게 보기
            </button>
          </div>

          {/* Modal */}
          {showModal && (
            <div 
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
              onClick={() => setShowModal(false)}
            >
              <div className="relative max-w-[95vw] max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-4xl"></i>
                </button>
                <img 
                  src="https://static.readdy.ai/image/3b7ebc9e513f68fc027b047fbdd0fbe7/566f0a1d64a13a9f2cb81c67695549a9.png"
                  alt="동호수배치도 확대"
                  className="max-w-full max-h-[90vh] object-contain rounded-lg"
                />
              </div>
            </div>
          )}

          <p className="text-[10px] md:text-[13px] text-gray-400 mt-2 md:mt-4 text-center">
            ※ 상기 동호수배치도는 설계 변경 등에 따라 변경될 수 있으며, 실제와 다를 수 있습니다.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 md:py-20 bg-gradient-to-br from-[#1a2b5c] to-[#0f1a3d] relative overflow-hidden" ref={ctaRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center relative z-10">
          <h2 className="text-[16px] md:text-[38px] font-bold text-white mb-3 md:mb-6" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            더 자세한 상담이 필요하신가요?
          </h2>
          <p className="text-[11px] md:text-[17px] text-white/90 mb-6 md:mb-10">
            전문 상담사가 친절하게 안내해드리겠습니다
          </p>
          <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
            <Link 
              to="/#inquiry"
              onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
              className="px-5 md:px-10 py-2.5 md:py-4 bg-white text-[#1a2b5c] rounded-full text-[11px] md:text-[16px] font-semibold hover:bg-gray-100 transition-all shadow-xl whitespace-nowrap"
            >
              방문예약 신청
            </Link>
            <a 
              href="tel:1555-5475"
              onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
              className="px-5 md:px-10 py-2.5 md:py-4 bg-transparent text-white rounded-full text-[11px] md:text-[16px] font-semibold hover:bg-white/10 transition-all border-2 border-white whitespace-nowrap"
            >
              전화 상담하기
            </a>
          </div>
        </div>
      </section>

      {/* 모바일 고정 플로팅 CTA 바 */}
      <div className="fixed bottom-0 left-0 right-0 z-[90] md:hidden">
        <div className="h-[2px] bg-gradient-to-r from-[#b8972a] via-[#f0d060] to-[#b8972a] cta-glow"></div>
        <div className="bg-gradient-to-r from-[#0a1229] via-[#1a2b5c] to-[#0a1229] px-3 py-3 flex items-center gap-2.5 shadow-2xl">
          <Link
            to="/reservation"
            onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group cta-pulse"
            style={{ background: 'linear-gradient(135deg, #b8972a 0%, #f0d060 40%, #c9a84c 70%, #b8972a 100%)' }}
          >
            <div className="absolute inset-0 bg-white/0 group-active:bg-white/10 transition-all"></div>
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent cta-shimmer"></div>
            </div>
            <i className="ri-calendar-check-line text-[#0a1229] text-[18px] relative z-10"></i>
            <span className="text-[14px] font-bold text-[#0a1229] relative z-10">방문예약</span>
          </Link>
          <a
            href="tel:1555-5475"
            onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-white rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group border-2 border-white/30"
          >
            <div className="absolute inset-0 bg-[#1a2b5c]/0 group-active:bg-[#1a2b5c]/10 transition-all"></div>
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-[#1a2b5c]/15 to-transparent cta-shimmer" style={{ animationDelay: '1.1s' }}></div>
            </div>
            <i className="ri-phone-fill text-[#1a2b5c] text-[18px] relative z-10"></i>
            <span className="text-[14px] font-bold text-[#1a2b5c] relative z-10">전화상담</span>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
